import { ColumnSizing } from '../tables-context';
import { MRT_ColumnDef } from 'material-react-table';
import { IPreparedMerchantDataProps } from '../../../components/merchants/MerchantPage/MerchantsListComponent';
import React, { FC } from 'react';
import { IAuthState } from '../../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../../setup';
import { ADMIN_GROUPS } from '../../../views/login/Login';
import { TableCell } from '../../../components/table/TableCell';
import { MERCHANTS_TABS } from '../../../utils/merchantUtils';
import { WhisperComponent } from '../../../components/developers/page-components/WhisperComponent';
import { Link, useLocation } from 'react-router-dom';
import { Title } from '../../../components/developers/page-components/Title';
import clsx from 'clsx';
import { StatusBadge } from '../../../components/status/StatusBadge';
import { CopyComponent } from '../../../components/CopyComponent';
import { DateComponent } from '../../../components/DateComponent';
import { useTranslation } from 'react-i18next';

export enum MerchantTableAccessorKey {
	id = 'id',
	date = 'date',
	merchantName = 'merchantName',
	partnerName = 'partnerName',
	integrationPartnerName = 'integrationPartnerName',
	contactName = 'contactName',
	email = 'email',
	status = 'status',
}

export const defaultMerchantsColumnSizing: ColumnSizing = {
	[MerchantTableAccessorKey.id]: 150,
	[MerchantTableAccessorKey.date]: 90,
	[MerchantTableAccessorKey.merchantName]: 100,
	[MerchantTableAccessorKey.partnerName]: 100,
	[MerchantTableAccessorKey.integrationPartnerName]: 100,
	[MerchantTableAccessorKey.contactName]: 120,
	[MerchantTableAccessorKey.email]: 120,
	[MerchantTableAccessorKey.status]: 50,
};

interface Props {
	contactName: string;
	phone: string;
	email: string;
}
export const ContactSpeaker: FC<Props> = (props) => {
	const { contactName, phone, email } = props;

	const { t } = useTranslation();
	const fields = [
		{
			title: 'Name:',
			value: contactName,
		},
		{
			title: 'Phone:',
			value: phone,
			text: 'NEW_APPLICATION.LABEL.PHONE'
		},
		{
			title: 'Email:',
			value: email,
			text: 'NEW_APPLICATION.LABEL.EMAIL'
		}
	];
	 return (
			<>
				{
					fields.map(({ value, title, text }) => (
						<div className={clsx(text && 'd-flex justify-content-between align-items-end')} key={title}>
							<div className={clsx(text && 'mt-3')}>
								<Title title={title} additionalClass="text-muted fs-8"/>
								<div className="fw-bolder">
									{value}
								</div>
							</div>
							{
								text && <CopyComponent value={value} title={t(text)} additionalClass="ms-5 mb-1" />
							}
						</div>
					))
				}
			</>

 );
};

export const defaultMerchantsColumns: MRT_ColumnDef<IPreparedMerchantDataProps>[] = [
	{
		accessorKey: MerchantTableAccessorKey.id,
		header: 'Id',
		enableSorting: false,
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Id</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => {
			const { search } = useLocation();

			const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
			const isPartnerAdmin = authUser?.userData?.userGroups?.some(el => el === ADMIN_GROUPS.PARTNER_ADMIN);
			const isIntegrationPartnerAdmin = authUser?.userData?.userGroups?.some(el => el === ADMIN_GROUPS.INTEGRATION_PARTNER_ADMIN);
			const path = `/accounts/merchants/${renderedCellValue}/${MERCHANTS_TABS.BUSINESS}`;
			return (
				<TableCell
					additionalClass="d-flex align-items-center main-font-family text-black fs-7"
					density={table?.options?.state?.density}
					isTextOverflow={true}
				>
					<CopyComponent value={renderedCellValue} title="Merchant Id"/>
					{
						// TODO check partners permissions
						(isPartnerAdmin || isIntegrationPartnerAdmin)
							? <div className="fc-text-overflow ms-2">{renderedCellValue}</div>
							: (
								<Link
									to={path}
									state={{ search }}
									className="text-black fc-text-overflow ms-2"
								>
									{renderedCellValue}
								</Link>
							)
					}
				</TableCell>
			);
		}
	},
	{
		accessorKey: MerchantTableAccessorKey.date,
		header: 'Created date',
		Header: <span className="margin-left-0 text-muted fw-bolder main-font-family">Created date</span>,
		enableSorting: false,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<DateComponent
					value={renderedCellValue}
					placement="top"
					// isTooltip
					isTime
				/>
			</TableCell>
		)
	},
	{
		accessorKey: MerchantTableAccessorKey.merchantName,
		header: 'Merchant name',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Merchant name</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black fw-bolder fs-7"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<div className="fc-text-overflow">
					{renderedCellValue}
				</div>
			</TableCell>
		)
	},
	{
		accessorKey: MerchantTableAccessorKey.partnerName,
		header: 'Partner name',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Partner name</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black fw-bolder fs-7"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<div className="fc-text-overflow">
					{renderedCellValue ? renderedCellValue : '-'}
				</div>
			</TableCell>
		)
	},
	{
		accessorKey: MerchantTableAccessorKey.integrationPartnerName,
		header: 'Integration partner name',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Integration partner name</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="main-font-family text-black fw-bolder fs-7"
				density={table?.options?.state?.density}
				isTextOverflow={true}
			>
				<div className="fc-text-overflow">
					{renderedCellValue ? renderedCellValue : '-'}
				</div>
			</TableCell>
		)
	},
	{
		accessorKey: MerchantTableAccessorKey.contactName,
		header: 'Contact name',
		enableSorting: false,
		Header: <span className="text-muted fw-bolder main-font-family">Contact name</span>,
		Cell: ({ renderedCellValue, table, row }: any): JSX.Element => {
			const contactName = row?.original?.contactName ?? '';
			const email = row?.original?.email ?? '';
			const phone = row?.original?.phone ?? '';

			return (
				<TableCell
					additionalClass="main-font-family text-black fs-7"
					density={table?.options?.state?.density}
					isTextOverflow={false}
				>
					<WhisperComponent
						whisperText={ContactSpeaker({ contactName, email, phone })}
						mainText={renderedCellValue}
						trigger="click"
						additionalClass="cursor-pointer fc-text-overflow d-block text-decoration-underline"
					/>
				</TableCell>
			);
		}
	},
	// {
	// 	accessorKey: MerchantTableAccessorKey.email,
	// 	header: 'Email',
	// 	enableSorting: false,
	// 	Header: <span className="text-muted fw-bolder main-font-family">Email</span>,
	// 	Cell: ({ renderedCellValue, table }: any): JSX.Element => (
	// 		<TableCell
	// 			additionalClass="main-font-family text-black fs-7"
	// 			density={table?.options?.state?.density}
	// 			isTextOverflow={true}
	// 		>
	// 			<div className="fc-text-overflow">{renderedCellValue}</div>
	// 		</TableCell>
	// 	)
	// },
	{
		accessorKey: MerchantTableAccessorKey.status,
		header: 'Status',
		enableSorting: false,
		// enableResizing: false,
		Header: <span className="text-muted fw-bolder main-font-family">Status</span>,
		Cell: ({ renderedCellValue, table }: any): JSX.Element => (
			<TableCell
				additionalClass="fs-6"
				density={table?.options?.state?.density}
				isTextOverflow={false}
			>
				<StatusBadge status={renderedCellValue}/>
			</TableCell>
		)
	}

];
